// Per-store locale config (injected by layout/theme.liquid).
const SHOP_DEFAULT_LOCALE = ((typeof window !== "undefined" && window.shopDefaultLocale) || "de").toLowerCase();
const SHOP_PUBLISHED_LOCALES = (typeof window !== "undefined" && Array.isArray(window.shopPublishedLocales) && window.shopPublishedLocales.length)
  ? window.shopPublishedLocales.map((l) => String(l).toLowerCase())
  : ["de", "en", "fr", "it"];
const SECONDARY_LOCALES = SHOP_PUBLISHED_LOCALES.filter((l) => l !== SHOP_DEFAULT_LOCALE);
// Sentinel alternation when no secondaries exist so the regexes never match.
const _SEC_ALT = SECONDARY_LOCALES.length ? SECONDARY_LOCALES.join("|") : "__never__";
const LANG_PREFIX_RE = new RegExp(`^/(?:${_SEC_ALT})(?:/|$)`);
const LANG_STRIP_RE = new RegExp(`^/(?:${_SEC_ALT})`);
const LANG_STRIP_OPT_RE = new RegExp(`^/(?:${_SEC_ALT})?`);
const LANG_CAPTURE_RE = new RegExp(`^/(${_SEC_ALT})`);
const LANG_CAPTURE_EXACT_RE = new RegExp(`^/(${_SEC_ALT})$`);

// Skip all language enforcement + the country/language modal inside the Shopify
// theme editor. The editor preview reloads on every content edit and its
// cross-origin iframe doesn't persist the nile_prefs cookie, so the modal would
// re-appear on every edit while the URL rewrites and location.reload() calls
// below would fight the editor. designMode is true only in the theme editor.
const IN_THEME_EDITOR = !!(
  typeof window !== "undefined" &&
  window.Shopify &&
  window.Shopify.designMode
);

class LanguageEnforcement {
  constructor() {
    this.supportedLanguages = ["de", "en", "fr", "it"];
    this.defaultLanguage = SHOP_DEFAULT_LOCALE;
    this.publishedLocales = SHOP_PUBLISHED_LOCALES;
    this.secondaryLocales = SECONDARY_LOCALES;
    this.isInitialized = false;
    this.hasEnforcedLanguage = false;
    this.init();
  }

  // True iff `lang` is a published secondary locale on this store.
  _canPrefix(lang) {
    return lang && lang !== this.defaultLanguage && this.secondaryLocales.includes(lang);
  }

  init() {
    this.setupLanguageChangeListeners();

    const urlParams = new URLSearchParams(window.location.search);
    const modalLanguage = urlParams.get("modal_language");

    if (modalLanguage && this.supportedLanguages.includes(modalLanguage)) {
      localStorage.setItem("selectedLanguage", modalLanguage);
      localStorage.setItem("userManuallySelected", modalLanguage);

      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("modal_language");
      window.history.replaceState({}, "", newUrl);

      this.enforceLanguageInstantly();
      return;
    }

    const userManuallySelected = localStorage.getItem("userManuallySelected");
    if (userManuallySelected === this.defaultLanguage) {
      const currentPath = window.location.pathname;
      const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);
      if (hasLanguagePrefix) {
        const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
        const newUrl =
          window.location.origin +
          pathWithoutLang +
          window.location.search +
          window.location.hash;
        window.history.replaceState({}, "", newUrl);

        this.hasEnforcedLanguage = true;
        this.lastEnforcedPath = window.location.pathname;
        return;
      }
    }

    this.enforceLanguageInstantly();
  }

  enforceLanguageInstantly() {
    try {
      const userManuallySelected = localStorage.getItem("userManuallySelected");

      if (userManuallySelected === this.defaultLanguage) {
        const currentPath = window.location.pathname;
        const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);
        if (hasLanguagePrefix) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);

          this.hasEnforcedLanguage = true;
          this.lastEnforcedPath = window.location.pathname;
          return;
        }
        this.hasEnforcedLanguage = true;
        return;
      }

      if (
        this.hasEnforcedLanguage &&
        window.location.pathname !== this.lastEnforcedPath
      ) {
        this.lastEnforcedPath = window.location.pathname;
        this.hasEnforcedLanguage = false;
      }

      if (this.hasEnforcedLanguage) {
        return;
      }

      const currentPath = window.location.pathname;
      const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);
      const storedLanguage = localStorage.getItem("selectedLanguage");
      const browserLanguage = this.getBrowserLanguage();
      let targetLanguage = this.defaultLanguage;

      if (storedLanguage && this.supportedLanguages.includes(storedLanguage)) {
        targetLanguage = storedLanguage;
      } else if (this.supportedLanguages.includes(browserLanguage)) {
        targetLanguage = browserLanguage;
        if (!storedLanguage && !userManuallySelected) {
          localStorage.setItem("selectedLanguage", targetLanguage);
        }
      }

      if (
        userManuallySelected &&
        this.supportedLanguages.includes(userManuallySelected)
      ) {
        targetLanguage = userManuallySelected;
        localStorage.setItem("selectedLanguage", userManuallySelected);
      }

      const currentUrlLanguage = currentPath.match(LANG_CAPTURE_RE)?.[1];

      if (targetLanguage === this.defaultLanguage) {
        if (hasLanguagePrefix) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
          this.hasEnforcedLanguage = true;
          window.location.reload();
        } else {
          this.hasEnforcedLanguage = true;
        }
      } else if (!this.secondaryLocales.includes(targetLanguage)) {
        // Target lang not published as secondary — skip redirect to avoid 404.
        this.hasEnforcedLanguage = true;
      } else {
        if (!hasLanguagePrefix || currentUrlLanguage !== targetLanguage) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            "/" +
            targetLanguage +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
          this.hasEnforcedLanguage = true;
          window.location.reload();
        } else {
          this.hasEnforcedLanguage = true;
        }
      }
    } catch (error) {
      console.error("Error in enforceLanguageInstantly:", error);
    }
  }

  enforceLanguage() {
    try {
      if (this.isInitialized) return;

      const userManuallySelected = localStorage.getItem("userManuallySelected");

      if (userManuallySelected === this.defaultLanguage) {
        const currentPath = window.location.pathname;
        const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);
        if (hasLanguagePrefix) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
        }
        this.isInitialized = true;
        return;
      }

      const currentPath = window.location.pathname;
      const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);
      const storedLanguage = localStorage.getItem("selectedLanguage");
      const browserLanguage = this.getBrowserLanguage();
      let targetLanguage = this.defaultLanguage;

      if (storedLanguage && this.supportedLanguages.includes(storedLanguage)) {
        targetLanguage = storedLanguage;
      } else if (this.supportedLanguages.includes(browserLanguage)) {
        targetLanguage = browserLanguage;
        if (!userManuallySelected) {
          localStorage.setItem("selectedLanguage", targetLanguage);
        }
      }

      if (
        userManuallySelected &&
        this.supportedLanguages.includes(userManuallySelected)
      ) {
        targetLanguage = userManuallySelected;
        localStorage.setItem("selectedLanguage", userManuallySelected);
      }

      if (targetLanguage === this.defaultLanguage) {
        if (hasLanguagePrefix) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
        }
        this.updateShopifyLanguage(this.defaultLanguage);
      } else if (!this.secondaryLocales.includes(targetLanguage)) {
        // Target lang not published as secondary — fall back to default.
        this.updateShopifyLanguage(this.defaultLanguage);
      } else {
        if (!hasLanguagePrefix) {
          const pathWithoutLang = currentPath === "/" ? "" : currentPath;
          const newUrl =
            window.location.origin +
            "/" +
            targetLanguage +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
        } else {
          const currentUrlLanguage = currentPath.match(LANG_CAPTURE_RE)?.[1];
          if (currentUrlLanguage !== targetLanguage) {
            const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
            const newUrl =
              window.location.origin +
              "/" +
              targetLanguage +
              pathWithoutLang +
              window.location.search +
              window.location.hash;
            window.history.replaceState({}, "", newUrl);
          }
        }
        this.updateShopifyLanguage(targetLanguage);
      }

      this.updateAllLanguageElements(targetLanguage);
      this.handleUrlWithoutLanguageHandle();

      setTimeout(() => {
        this.refreshFooterLanguageSelector(targetLanguage);
      }, 500);

      this.isInitialized = true;
    } catch (error) {
      console.error("Error in enforceLanguage:", error);
    }
  }

  handleUrlWithoutLanguageHandle() {
    const currentPath = window.location.pathname;
    const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);

    if (!hasLanguagePrefix) {
      const userManuallySelected = localStorage.getItem("userManuallySelected");

      if (userManuallySelected === this.defaultLanguage) {
        return;
      }

      const storedLanguage = localStorage.getItem("selectedLanguage");
      const browserLanguage = this.getBrowserLanguage();
      let targetLanguage = this.defaultLanguage;

      if (storedLanguage && this.supportedLanguages.includes(storedLanguage)) {
        targetLanguage = storedLanguage;
      } else if (this.supportedLanguages.includes(browserLanguage)) {
        targetLanguage = browserLanguage;
      }

      if (targetLanguage !== this.defaultLanguage) {
        const pathWithoutLang = currentPath === "/" ? "" : currentPath;
        const newUrl =
          window.location.origin +
          "/" +
          targetLanguage +
          pathWithoutLang +
          window.location.search +
          window.location.hash;
        window.history.replaceState({}, "", newUrl);
        this.updateShopifyLanguage(targetLanguage);
      }
    }
  }

  getBrowserLanguage() {
    const browserLanguages = navigator.languages || [navigator.language];
    const languageMapping = {
      de: "de",
      "de-DE": "de",
      "de-AT": "de",
      "de-CH": "de",
      en: "en",
      "en-US": "en",
      "en-GB": "en",
      "en-CA": "en",
      fr: "fr",
      "fr-FR": "fr",
      "fr-CH": "fr",
      "fr-CA": "fr",
      it: "it",
      "it-IT": "it",
      "it-CH": "it",
    };

    for (const lang of browserLanguages) {
      const baseLang = lang.split("-")[0];
      const mappedLang = languageMapping[lang] || languageMapping[baseLang];
      if (mappedLang) return mappedLang;
    }
    return this.defaultLanguage;
  }

  updateShopifyLanguage(language) {
    try {
      const languageForm =
        document.getElementById("FooterLanguageForm") ||
        document.querySelector('form[action*="localization"]');

      if (languageForm) {
        const languageInput = languageForm.querySelector(
          'input[name="locale_code"]'
        );
        if (languageInput && languageInput.value !== language) {
          languageInput.value = language;
          languageInput.dispatchEvent(new Event("change", { bubbles: true }));

          document
            .querySelectorAll('input[name="locale_code"]')
            .forEach((input) => {
              if (input.value !== language) {
                input.value = language;
                input.dispatchEvent(new Event("change", { bubbles: true }));
              }
            });
        }
      }

      const footerLanguageSelector = document.querySelector(
        ".footer-language-selector, .language-selector, [data-language-selector]"
      );
      if (footerLanguageSelector) {
        footerLanguageSelector.value = language;
        footerLanguageSelector.dispatchEvent(
          new Event("change", { bubbles: true })
        );
      }

      const disclosureButton = document.querySelector(
        ".disclosure__button[data-value]"
      );
      if (disclosureButton) {
        disclosureButton.setAttribute("data-value", language);
      }

      const disclosureLinks = document.querySelectorAll(
        ".disclosure__link[data-value]"
      );
      disclosureLinks.forEach((link) => {
        if (link.getAttribute("data-value") === language) {
          link.classList.add("active");
        } else {
          link.classList.remove("active");
        }
      });

      this.updateLanguageDisplay(language);

      setTimeout(() => {
        this.forceShopifyLanguageChange(language);
      }, 200);
    } catch (error) {
      console.error("Error updating Shopify language:", error);
    }
  }

  forceShopifyLanguageChange(language) {
    try {
      const forms = document.querySelectorAll('form[action*="localization"]');

      forms.forEach((form, index) => {
        const input = form.querySelector('input[name="locale_code"]');
        if (input && input.value !== language) {
          input.value = language;

          const changeEvent = new Event("change", { bubbles: true });
          const inputEvent = new Event("input", { bubbles: true });

          input.dispatchEvent(inputEvent);
          input.dispatchEvent(changeEvent);
        }
      });
    } catch (error) {
      console.error("Error forcing Shopify language change:", error);
    }
  }

  updateLanguageDisplay(language) {
    try {
      const languageNames = {
        de: "Deutsch",
        en: "English",
        fr: "Français",
        it: "Italiano",
      };

      const disclosureButtons = document.querySelectorAll(
        ".disclosure__button span"
      );
      if (disclosureButtons.length > 0) {
        disclosureButtons.forEach((span) => {
          if (
            span.textContent &&
            Object.values(languageNames).includes(span.textContent.trim())
          ) {
            span.textContent = languageNames[language];
          }
        });
      }

      const footerButton = document.querySelector(".disclosure__button");
      if (footerButton) {
        const buttonText = footerButton.querySelector("span");
        if (buttonText) {
          buttonText.textContent = languageNames[language];
        }
      }

      const footerLinks = document.querySelectorAll(".disclosure__link");
      footerLinks.forEach((link) => {
        const linkValue = link.getAttribute("data-value");
        if (linkValue === language) {
          link.classList.add("active");
          link.setAttribute("aria-current", "true");
        } else {
          link.classList.remove("active");
          link.removeAttribute("aria-current");
        }
      });
    } catch (error) {
      console.error("Error updating language display:", error);
    }
  }

  refreshFooterLanguageSelector(language) {
    try {
      const languageNames = {
        de: "Deutsch",
        en: "English",
        fr: "Français",
        it: "Italiano",
      };

      const footerDisclosure = document.querySelector(".disclosure__button");
      if (footerDisclosure) {
        const buttonText = footerDisclosure.querySelector("span");
        if (buttonText) {
          buttonText.textContent = languageNames[language];
        }
        footerDisclosure.setAttribute("data-value", language);
      }

      const allDisclosureLinks = document.querySelectorAll(".disclosure__link");
      allDisclosureLinks.forEach((link) => {
        const linkValue = link.getAttribute("data-value");
        if (linkValue === language) {
          link.classList.add("active");
          link.setAttribute("aria-current", "true");
        } else {
          link.classList.remove("active");
          link.removeAttribute("aria-current");
        }
      });

      const footerForm = document.querySelector('form[action*="localization"]');
      if (footerForm) {
        const formInput = footerForm.querySelector('input[name="locale_code"]');
        if (formInput && formInput.value !== language) {
          formInput.value = language;
          formInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      const footerSelectors = document.querySelectorAll(
        ".footer-language-selector, .language-selector, [data-language-selector]"
      );
      footerSelectors.forEach((selector) => {
        if (selector.value !== language) {
          selector.value = language;
          selector.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });

      this.forceShopifyLanguageChange(language);
      this.submitLocalizationForm(language);
    } catch (error) {
      console.error("Error refreshing footer language selector:", error);
    }
  }

  submitLocalizationForm(language) {
    try {
      const forms = document.querySelectorAll('form[action*="localization"]');
      forms.forEach((form, index) => {
        const input = form.querySelector('input[name="locale_code"]');
        if (input && input.value !== language) {
          input.value = language;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    } catch (error) {
      console.error("Error updating localization form:", error);
    }
  }

  updateAllLanguageElements(language) {
    try {
      const languageSelectors = document.querySelectorAll(
        ".footer-language-selector, .language-selector, [data-language-selector]"
      );
      if (languageSelectors.length > 0) {
        languageSelectors.forEach((dropdown) => {
          const selectedOption = dropdown.querySelector(
            `option[value="${language}"], [data-value="${language}"]`
          );
          if (selectedOption) {
            dropdown.value = language;
            dropdown.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }

      const languageButtons = document.querySelectorAll(
        "[data-language], .language-button"
      );
      if (languageButtons.length > 0) {
        languageButtons.forEach((button) => {
          const buttonLanguage =
            button.getAttribute("data-language") || button.dataset.language;
          if (buttonLanguage === language) {
            button.classList.add("active", "selected");
          } else {
            button.classList.remove("active", "selected");
          }
        });
      }

      if (typeof updateModalContent === "function") {
        updateModalContent(language);
      }
    } catch (error) {
      console.error("Error updating language elements:", error);
    }
  }

  setupLanguageChangeListeners() {
    try {
      document.addEventListener("change", (e) => {
        if (
          e.target.closest('form[action*="addresses"]') ||
          e.target.closest(".edit-form-card-content-item") ||
          e.target.name === "address[country]" ||
          e.target.name === "address[province]" ||
          e.target.name === "customer[country]" ||
          e.target.name === "customer[province]"
        ) {
          return;
        }

        if (
          e.target.matches(
            ".footer-language-selector, .language-selector, [data-language-selector]"
          )
        ) {
          const newLanguage = e.target.value;
          if (newLanguage && this.supportedLanguages.includes(newLanguage)) {
            this.handleLanguageChange(newLanguage);
          }
        }
      });

      document.addEventListener("click", (e) => {
        if (e.target.matches("[data-language], .language-button")) {
          const language =
            e.target.getAttribute("data-language") || e.target.dataset.language;
          if (language && this.supportedLanguages.includes(language)) {
            e.preventDefault();
            this.handleLanguageChange(language);
          }
        }
      });

      document.addEventListener("click", (e) => {
        if (e.target.closest(".language-modal-container")) {
          return;
        }

        if (
          e.target.closest('form[action*="addresses"]') ||
          e.target.closest(".edit-form-card-content-item") ||
          e.target.closest(".edit-form-card") ||
          e.target.closest(".address-form") ||
          e.target.closest(".customer-form")
        ) {
          return;
        }

        let target = e.target;
        let language = null;

        for (let i = 0; i < 3 && target; i++) {
          if (
            target.classList &&
            target.classList.contains("disclosure__link") &&
            target.hasAttribute("data-value") &&
            !target.closest("#ModalLanguageCountryList")
          ) {
            language = target.getAttribute("data-value");
            if (language && this.supportedLanguages.includes(language)) {
              localStorage.setItem("selectedLanguage", language);

              localStorage.setItem("userManuallySelected", language);

              this.hasEnforcedLanguage = false;
              this.lastEnforcedPath = null;

              this.handleLanguageChange(language);
              return;
            }
          }
          target = target.parentElement;
        }
      });

      document.addEventListener("submit", (e) => {
        if (
          e.target.closest('form[action*="addresses"]') ||
          e.target.closest(".edit-form-card-content-item") ||
          e.target.closest('form[action*="customer"]') ||
          e.target.closest('form[action*="account"]') ||
          e.target.closest(".address-form") ||
          e.target.closest(".customer-form") ||
          e.target.closest(".edit-form-card")
        ) {
          return;
        }

        if (e.target.action && e.target.action.includes("localization")) {
          const languageInput = e.target.querySelector(
            'input[name="locale_code"]'
          );
          if (languageInput) {
            const language = languageInput.value;
            if (language && this.supportedLanguages.includes(language)) {
              localStorage.setItem("selectedLanguage", language);

              localStorage.setItem("userManuallySelected", language);
            }
          }
        }
      });

      document.addEventListener("click", (e) => {
        if (e.target.closest(".language-modal-container")) {
          return;
        }

        if (
          e.target.closest('form[action*="addresses"]') ||
          e.target.closest(".edit-form-card-content-item") ||
          e.target.closest('form[action*="customer"]') ||
          e.target.closest('form[action*="account"]') ||
          e.target.closest(".edit-form-card") ||
          e.target.closest(".address-form") ||
          e.target.closest(".customer-form")
        ) {
          return;
        }

        let target = e.target;
        let language = null;

        for (let i = 0; i < 3 && target; i++) {
          if (target.hasAttribute("data-value")) {
            language = target.getAttribute("data-value");
            if (this.supportedLanguages.includes(language)) {
              e.preventDefault();
              e.stopPropagation();

              localStorage.setItem("selectedLanguage", language);

              localStorage.setItem("userManuallySelected", language);

              this.hasEnforcedLanguage = false;
              this.lastEnforcedPath = null;

              this.handleLanguageChange(language);
              return;
            }
          }

          if (target.hasAttribute("data-locale")) {
            language = target.getAttribute("data-locale");
            if (this.supportedLanguages.includes(language)) {
              e.preventDefault();
              e.stopPropagation();

              localStorage.setItem("selectedLanguage", language);

              localStorage.setItem("userManuallySelected", language);

              this.hasEnforcedLanguage = false;
              this.lastEnforcedPath = null;

              this.handleLanguageChange(language);
              return;
            }
          }

          if (target.hasAttribute("href")) {
            const href = target.getAttribute("href");
            const langMatch = href.match(LANG_CAPTURE_EXACT_RE);
            if (langMatch) {
              language = langMatch[1];
              if (this.supportedLanguages.includes(language)) {
                e.preventDefault();
                e.stopPropagation();

                localStorage.setItem("selectedLanguage", language);

                this.hasEnforcedLanguage = false;
                this.lastEnforcedPath = null;

                this.handleLanguageChange(language);
                return;
              }
            }
          }

          target = target.parentElement;
        }
      });

      document.addEventListener("click", (e) => {
        if (e.target.closest(".language-modal-container")) {
          return;
        }

        if (
          e.target.closest('form[action*="addresses"]') ||
          e.target.closest(".edit-form-card-content-item") ||
          e.target.closest('form[action*="customer"]') ||
          e.target.closest('form[action*="account"]') ||
          e.target.closest(".edit-form-card") ||
          e.target.closest(".address-form") ||
          e.target.closest(".customer-form")
        ) {
          return;
        }

        let target = e.target;

        for (let i = 0; i < 3 && target; i++) {
          if (
            target.classList &&
            target.classList.contains("disclosure__link") &&
            !target.closest("#ModalLanguageCountryList")
          ) {
            const dataValue = target.getAttribute("data-value");
            if (dataValue && this.supportedLanguages.includes(dataValue)) {
              e.preventDefault();
              e.stopPropagation();

              localStorage.setItem("selectedLanguage", dataValue);

              localStorage.setItem("userManuallySelected", dataValue);

              this.hasEnforcedLanguage = false;
              this.lastEnforcedPath = null;

              this.handleLanguageChange(dataValue);
              return;
            }
          }
          target = target.parentElement;
        }
      });
    } catch (error) {
      console.error("Error setting up language change listeners:", error);
    }
  }

  handleLanguageChange(newLanguage) {
    try {
      // Lang not published here: sync UI/form but don't navigate (would 404).
      if (
        newLanguage !== this.defaultLanguage &&
        !this.secondaryLocales.includes(newLanguage)
      ) {
        this.updateAllLanguageElements(newLanguage);
        this.updateShopifyLanguage(newLanguage);
        return;
      }

      this.hasEnforcedLanguage = false;
      this.lastEnforcedPath = null;

      this.updateAllLanguageElements(newLanguage);

      const currentPath = window.location.pathname;
      const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);

      if (newLanguage === this.defaultLanguage) {
        if (hasLanguagePrefix) {
          const pathWithoutLang = currentPath.replace(LANG_STRIP_RE, "");
          const newUrl =
            window.location.origin +
            pathWithoutLang +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);

          this.hasEnforcedLanguage = true;
          this.lastEnforcedPath = window.location.pathname;
        }
      } else {
        let newPath;
        if (hasLanguagePrefix) {
          newPath = currentPath.replace(LANG_STRIP_RE, `/${newLanguage}`);
        } else {
          newPath = `/${newLanguage}${currentPath}`;
        }
        const newUrl =
          window.location.origin +
          newPath +
          window.location.search +
          window.location.hash;
        window.history.replaceState({}, "", newUrl);
      }

      this.updateShopifyLanguage(newLanguage);

      if (newLanguage === this.defaultLanguage) {
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error("Error in handleLanguageChange:", error);
    }
  }
}

let languageEnforcement;
if (!IN_THEME_EDITOR) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      languageEnforcement = new LanguageEnforcement();
    });
  } else {
    languageEnforcement = new LanguageEnforcement();
  }
}

document.addEventListener("DOMContentLoaded", async () => {
  // Never run the country/language modal inside the theme editor (see IN_THEME_EDITOR).
  if (IN_THEME_EDITOR) return;
  const urlParams = new URLSearchParams(window.location.search);
  const wasRedirected = urlParams.get("redirected") === "true";
  const modalCountry = urlParams.get("modal_country");
  const modalLanguage = urlParams.get("modal_language");

  console.log("[NILE-DEBUG] modal DOMContentLoaded", {
    host: window.location.hostname,
    href: window.location.href,
    wasRedirected,
    modalCountry,
    modalLanguage,
    nileRedirectInProgress: window.nileRedirectInProgress,
    existingPrefs: window.NileCountryRedirect
      ? window.NileCountryRedirect.getNilePrefs()
      : "no-API",
    currentStore: window.NileCountryRedirect
      ? window.NileCountryRedirect.getCurrentStore()
      : "no-API",
  });

  // =====================================================
  // EARLY EXIT: Check if redirect is in progress from nile-country-redirect.js
  // =====================================================
  if (window.nileRedirectInProgress) {
    console.log("[NILE-DEBUG] modal: nileRedirectInProgress=true -> bail (a redirect is already happening)");
    return; // Redirect is happening, don't show modal
  }

  // =====================================================
  // EARLY EXIT: Check for existing cookie - if valid, skip modal entirely
  // =====================================================
  if (window.NileCountryRedirect) {
    const existingPrefs = window.NileCountryRedirect.getNilePrefs();
    if (existingPrefs && existingPrefs.country && existingPrefs.store) {
      const currentStore = window.NileCountryRedirect.getCurrentStore();
      // User has preferences and is on correct store - skip modal
      if (currentStore === existingPrefs.store || currentStore === 'unknown') {
        // Clean up URL params if present
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete("redirected");
        newUrl.searchParams.delete("country");
        newUrl.searchParams.delete("modal_country");
        newUrl.searchParams.delete("modal_language");
        if (newUrl.toString() !== window.location.href) {
          window.history.replaceState({}, "", newUrl);
        }
        console.log("[NILE-DEBUG] modal: cookie-exit (already on correct store), no redirect");
        return; // Skip modal - user already made a choice
      }
      console.log("[NILE-DEBUG] modal: existing cookie store != currentStore (expects nile-country-redirect to have redirected)", {
        cookieStore: existingPrefs.store,
        currentStore: window.NileCountryRedirect.getCurrentStore(),
      });
      // If on wrong store, nile-country-redirect.js should have already redirected
    }
  }

  if (modalCountry && modalLanguage) {
    applyModalSelections(modalCountry, modalLanguage);

    // Set cookie via NileCountryRedirect (cookie only, no localStorage)
    if (window.NileCountryRedirect) {
      const targetStore = window.NileCountryRedirect.getStoreForCountry(modalCountry);
      window.NileCountryRedirect.setNilePrefs({
        country: modalCountry,
        language: modalLanguage,
        store: targetStore
      });
    }

    const newUrl = new URL(window.location);
    newUrl.searchParams.delete("modal_country");
    newUrl.searchParams.delete("modal_language");
    window.history.replaceState({}, "", newUrl);
    return;
  }

  if (wasRedirected) {
    console.log("[NILE-DEBUG] modal: wasRedirected branch -> set cookie if missing, clean URL, no redirect");
    // Also grab the country from URL and set cookie if not already set
    const redirectedCountry = urlParams.get("country");
    if (redirectedCountry && window.NileCountryRedirect) {
      const existingPrefs = window.NileCountryRedirect.getNilePrefs();
      const targetStore = window.NileCountryRedirect.getStoreForCountry(redirectedCountry);
      // Prefer the language LanguageEnforcement just applied from modal_language
      // (it stores it in localStorage) so the cookie matches the page's language.
      const supportedLangs = ["de", "en", "fr", "it"];
      const appliedLang = localStorage.getItem("selectedLanguage");
      const language =
        (appliedLang && supportedLangs.includes(appliedLang) && appliedLang) ||
        (existingPrefs && existingPrefs.language) ||
        window.NileCountryRedirect.getBrowserLanguage();
      // Always reconcile the cookie to THIS destination store. Overwriting a
      // stale/conflicting store value (e.g. a pre-rollout {store:'chf'} for GB
      // sitting on this domain) is required so checkAndRedirect won't bounce on
      // the next navigation.
      console.log("[NILE-DEBUG] modal: wasRedirected -> reconcile cookie", {
        redirectedCountry,
        targetStore,
        language,
        prevStore: existingPrefs && existingPrefs.store,
      });
      window.NileCountryRedirect.setNilePrefs({
        country: redirectedCountry,
        language: language,
        store: targetStore,
      });
    }
    
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete("redirected");
    newUrl.searchParams.delete("country");
    window.history.replaceState({}, "", newUrl);
    return;
  }

  function getBrowserLanguage() {
    const browserLanguages = navigator.languages || [navigator.language];
    const languageMapping = {
      de: "de",
      "de-DE": "de",
      "de-AT": "de",
      "de-CH": "de",
      en: "en",
      "en-US": "en",
      "en-GB": "en",
      "en-CA": "en",
      fr: "fr",
      "fr-FR": "fr",
      "fr-CH": "fr",
      "fr-CA": "fr",
      it: "it",
      "it-IT": "it",
      "it-CH": "it",
    };

    for (const lang of browserLanguages) {
      const baseLang = lang.split("-")[0];
      const mappedLang = languageMapping[lang] || languageMapping[baseLang];
      if (mappedLang) return mappedLang;
    }
    return "de";
  }

  function getCurrentEnvironment() {
    const currentHost = window.location.hostname;
    const shopDomain =
      window.shopDomain ||
      window.Shopify?.shop ||
      window.shop?.permanent_domain;

    // Map each configured store URL to its store id + test flag.
    const ts = window.themeSettings || {};
    const urlStoreMap = [
      { url: ts.test_ch_store_url, store: "chf", isTest: true },
      { url: ts.test_eu_store_url, store: "eur", isTest: true },
      { url: ts.test_gbp_store_url, store: "gbp", isTest: true },
      { url: ts.ch_store_url, store: "chf", isTest: false },
      { url: ts.eu_store_url, store: "eur", isTest: false },
      { url: ts.gbp_store_url, store: "gbp", isTest: false },
    ];

    if (currentHost.includes("myshopify.com")) {
      for (const entry of urlStoreMap) {
        if (entry.url && currentHost === new URL(entry.url).hostname) {
          return { isTest: entry.isTest, store: entry.store };
        }
      }
    }

    // Fallback: match window.shopDomain against the configured hostnames.
    if (shopDomain) {
      for (const entry of urlStoreMap) {
        if (entry.url && shopDomain === new URL(entry.url).hostname) {
          return { isTest: entry.isTest, store: entry.store };
        }
      }
    }

    return { isTest: false, store: "unknown" };
  }

  function applyModalSelections(country, language) {
    const countryForm = document.getElementById("LanguageModalCountryForm");
    const languageForm = document.getElementById("LanguageModalLanguageForm");

    if (countryForm && languageForm) {
      const countryInput = countryForm.querySelector(
        'input[name="country_code"]'
      );
      const languageInput = languageForm.querySelector(
        'input[name="locale_code"]'
      );

      if (countryInput && languageInput) {
        countryInput.value = country;
        languageInput.value = language;
        updateButtonTexts(country, language);
        updateModalContent(language);
      }
    }
  }

  function updateButtonTexts(country, language) {
    const countryButton = document.querySelector(
      "#LanguageModalCountryForm .disclosure__button"
    );
    if (countryButton) {
      const countryFlagSpan = countryButton.querySelector(".country-flag");
      if (countryFlagSpan) {
        const currentFlagImg = countryFlagSpan.querySelector("img");
        let baseUrl = "";
        if (currentFlagImg && currentFlagImg.src) {
          const urlParts = currentFlagImg.src.split("/");
          urlParts.pop();
          baseUrl = urlParts.join("/") + "/";
        } else {
          baseUrl = window.location.origin + "/assets/";
        }

        const flagData = {
          CH: { filename: "flag-ch.svg", alt: "Switzerland" },
          DE: { filename: "flag-de.svg", alt: "Germany" },
          AT: { filename: "flag-at.svg", alt: "Austria" },
          BE: { filename: "flag-be.svg", alt: "Belgium" },
          BG: { filename: "flag-bg.svg", alt: "Bulgaria" },
          HR: { filename: "flag-hr.svg", alt: "Croatia" },
          CZ: { filename: "flag-cz.svg", alt: "Czech Republic" },
          DK: { filename: "flag-dk.svg", alt: "Denmark" },
          EE: { filename: "flag-ee.svg", alt: "Estonia" },
          FI: { filename: "flag-fi.svg", alt: "Finland" },
          FR: { filename: "flag-fr.svg", alt: "France" },
          GR: { filename: "flag-gr.svg", alt: "Greece" },
          HU: { filename: "flag-hu.svg", alt: "Hungary" },
          IE: { filename: "flag-ie.svg", alt: "Ireland" },
          IT: { filename: "flag-it.svg", alt: "Italy" },
          LV: { filename: "flag-lv.svg", alt: "Latvia" },
          LT: { filename: "flag-lt.svg", alt: "Lithuania" },
          LU: { filename: "flag-lu.svg", alt: "Luxembourg" },
          MT: { filename: "flag-mt.svg", alt: "Malta" },
          NL: { filename: "flag-nl.svg", alt: "Netherlands" },
          NO: { filename: "flag-no.svg", alt: "Norway" },
          PL: { filename: "flag-pl.svg", alt: "Poland" },
          PT: { filename: "flag-pt.svg", alt: "Portugal" },
          RO: { filename: "flag-ro.svg", alt: "Romania" },
          SK: { filename: "flag-sk.svg", alt: "Slowakia" },
          SI: { filename: "flag-si.svg", alt: "Slovenia" },
          ES: { filename: "flag-es.svg", alt: "Spain" },
          SE: { filename: "flag-se.svg", alt: "Sweden" },
          LI: { filename: "flag-li.svg", alt: "Liechtenstein" },
          CA: { filename: "flag-ca.svg", alt: "Canada" },
          GB: { filename: "flag-gb.svg", alt: "United Kingdom" },
        };

        const countryData = flagData[country];
        if (countryData) {
          const flagUrl = baseUrl + countryData.filename;
          countryFlagSpan.innerHTML = `<img src="${flagUrl}" alt="${countryData.alt}" width="auto" height="14">`;
          countryFlagSpan.setAttribute("data-country-code", country);
        } else {
          countryFlagSpan.innerHTML =
            '<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.043 8.65234C14.0977 8.37891 14.125 8.07813 14.125 7.77734C14.125 4.66016 11.7734 2.08984 8.76562 1.70703L8.62891 2.19922C9.03906 2.08984 9.47656 2.25391 9.72266 2.60938C10.0781 3.12891 9.88672 3.83984 9.33984 4.11328L7.72656 4.90625C7.61719 4.96094 7.5625 5.07031 7.5625 5.17969C7.5625 5.42578 7.45312 5.67188 7.28906 5.83594L6.76953 6.35547C6.71484 6.41016 6.6875 6.49219 6.6875 6.57422V6.62891C6.6875 6.79297 6.79687 6.92969 6.96094 6.92969C7.07031 6.92969 7.15234 6.875 7.20703 6.79297L7.50781 6.41016C7.67187 6.19141 7.94531 6.05469 8.21875 6.05469C8.46484 6.05469 8.71094 6.13672 8.875 6.30078L8.95703 6.38281C9.01172 6.46484 9.14844 6.46484 9.20312 6.38281L10.0781 5.50781C10.4336 5.15234 11.0625 5.39844 11.0625 5.91797V6.16406C11.0625 6.32813 11.1992 6.46484 11.3633 6.46484H11.4727C12.1016 6.46484 12.457 7.23047 12.0469 7.72266C11.9648 7.85938 11.9648 8.02344 12.1016 8.13281L12.5391 8.48828C12.6758 8.59766 12.8398 8.65234 13.0039 8.65234H14.043ZM13.8516 9.52734H13.0039C12.6211 9.52734 12.2656 9.41797 11.9648 9.17188L11.5273 8.81641C11.1172 8.43359 11.0078 7.83203 11.2539 7.33984C10.7891 7.3125 10.4062 7.01172 10.2422 6.57422L9.83203 7.01172C9.42187 7.42188 8.73828 7.42188 8.32812 7.01172L8.24609 6.92969C8.24609 6.92969 8.24609 6.90234 8.21875 6.90234L8.19141 6.92969L7.89062 7.3125C7.67187 7.61328 7.34375 7.77734 6.96094 7.77734C6.33203 7.77734 5.8125 7.25781 5.8125 6.62891V6.57422C5.8125 6.24609 5.92187 5.94531 6.16797 5.72656L6.66016 5.20703C6.6875 5.20703 6.6875 5.20703 6.6875 5.17969C6.6875 4.74219 6.93359 4.33203 7.34375 4.14063L8.95703 3.32031C9.03906 3.29297 9.06641 3.18359 9.01172 3.10156C8.95703 3.04688 8.875 3.01953 8.82031 3.04688L8.60156 3.15625C8.10938 3.40234 7.53516 2.96484 7.67188 2.41797L7.86328 1.65234C4.52734 1.73438 1.875 4.44141 1.875 7.77734C1.875 11.1406 4.55469 13.875 7.89062 13.9023C7.75391 13.6563 7.50781 13.4648 7.17969 13.4648C6.74219 13.4648 6.30469 13.3008 6.00391 12.9727L5.56641 12.5352C5.15625 12.125 4.9375 11.5781 4.9375 11.0039V10.9219C4.9375 10.1836 5.29297 9.5 5.89453 9.08984L6.66016 8.59766C7.01562 8.35156 7.42578 8.21484 7.86328 8.21484H8.4375C8.95703 8.21484 9.44922 8.40625 9.85937 8.76172L10.5977 9.36328C10.707 9.47266 10.8711 9.52734 11.0352 9.52734C11.4453 9.52734 11.8281 9.69141 12.1289 9.99219L12.2383 10.1016C12.4297 10.293 12.7031 10.4023 12.9766 10.4023H13.5234C13.6602 10.1289 13.7695 9.82813 13.8516 9.52734ZM13.0039 11.2773V11.3047H12.9766C12.457 11.3047 11.9648 11.0859 11.6094 10.7305L11.5 10.6211C11.3633 10.4844 11.1992 10.4297 11.0352 10.4297C10.6523 10.4297 10.2969 10.293 10.0234 10.0469L9.28516 9.41797C9.03906 9.22656 8.73828 9.08984 8.4375 9.08984H7.86328C7.58984 9.08984 7.34375 9.17188 7.125 9.33594L6.38672 9.82812C6.00391 10.0742 5.78516 10.4844 5.78516 10.9219V11.0039C5.78516 11.3594 5.94922 11.6875 6.19531 11.9336L6.63281 12.3711C6.76953 12.5078 6.96094 12.5898 7.17969 12.5898C7.97266 12.5898 8.62891 13.1367 8.82031 13.8477C10.543 13.6289 12.0469 12.6719 13.0039 11.2773ZM8 14.7773C5.48437 14.7773 3.1875 13.4648 1.92969 11.2773C0.671875 9.11719 0.671875 6.46484 1.92969 4.27734C3.1875 2.11719 5.48438 0.777344 8 0.777344C10.4883 0.777344 12.7852 2.11719 14.043 4.27734C15.3008 6.46484 15.3008 9.11719 14.043 11.2773C12.7852 13.4648 10.4883 14.7773 8 14.7773ZM6.11328 3.53906C6.27734 3.70313 6.27734 4.00391 6.11328 4.16797L5.23828 5.04297C5.07422 5.20703 4.77344 5.20703 4.60938 5.04297C4.44531 4.87891 4.44531 4.57812 4.60938 4.41406L5.48438 3.53906C5.64844 3.375 5.94922 3.375 6.11328 3.53906Z" fill="currentColor"/></svg>';
        }
      }

      const countryNameSpan = countryButton.querySelector(
        "span:not(.country-flag)"
      );
      if (countryNameSpan) {
        const countryNames = {
          CH: "Schweiz",
          DE: "Deutschland",
          AT: "Österreich",
          BE: "Belgien",
          BG: "Bulgarien",
          HR: "Kroatien",
          CZ: "Tschechische Republik",
          DK: "Dänemark",
          EE: "Estland",
          FI: "Finnland",
          FR: "Frankreich",
          GR: "Griechenland",
          HU: "Ungarn",
          IE: "Irland",
          IT: "Italien",
          LV: "Lettland",
          LT: "Litauen",
          LU: "Luxemburg",
          MT: "Malta",
          NL: "Niederlande",
          NO: "Norwegen",
          PL: "Polen",
          PT: "Portugal",
          RO: "Rumänien",
          SK: "Slowakei",
          SI: "Slowenien",
          ES: "Spanien",
          SE: "Schweden",
          LI: "Liechtenstein",
          CA: "Kanada",
          GB: "Vereinigtes Königreich",
        };
        const countryName = countryNames[country] || country;
        countryNameSpan.textContent = countryName;
      }
    }

    const languageButton = document.querySelector(
      "#LanguageModalLanguageForm .disclosure__button"
    );
    if (languageButton && language) {
      const languageNames = {
        de: "Deutsch",
        en: "English",
        fr: "Français",
        it: "Italiano",
      };
      const languageName = languageNames[language] || language;
      const valueSpan = languageButton.querySelector(".value");
      if (valueSpan) {
        valueSpan.textContent = languageName;
      }
    }
  }

  function updateLanguageButtonOnly(language) {
    const languageButton = document.querySelector(
      "#LanguageModalLanguageForm .disclosure__button"
    );
    if (languageButton) {
      const languageSpan = languageButton.querySelector("span");
      if (languageSpan) {
        const languageNames = {
          de: "Deutsch",
          en: "English",
          fr: "Français",
          it: "Italiano",
        };
        languageSpan.textContent =
          languageNames[language] || language.toUpperCase();
      }
    }
  }

  function updateModalContent(language) {
    const modalTitle = document.getElementById("language-modal-title");
    const modalDescription = document.getElementById(
      "language-modal-description"
    );
    const continueButton = document.querySelector(
      ".language-modal-button span"
    );

    const titles = {
      de: "Wohin soll's gehen?",
      en: "Where to go?",
      fr: "Où aller?",
      it: "Dove andare?",
    };
    const descriptions = {
      de: "Bitte wähle deine Region und deine Sprache um fortzufahren",
      en: "Please select your language and region to continue",
      fr: "Veuillez sélectionner votre langue et votre région pour continuer",
      it: "Seleziona la tua lingua e la tua regione per continuare",
    };
    const continueTexts = {
      de: "Weiter",
      en: "Continue",
      fr: "Continuer",
      it: "Continua",
    };

    if (modalTitle) modalTitle.textContent = titles[language] || titles.en;
    if (modalDescription)
      modalDescription.textContent = descriptions[language] || descriptions.en;
    if (continueButton)
      continueButton.textContent = continueTexts[language] || continueTexts.en;
  }

  async function getUserCountry() {
    // Try Shopify's server-side detection first (most reliable, no API call)
    if (window.NileCountryRedirect) {
      const shopifyCountry = window.NileCountryRedirect.getShopifyDetectedCountry();
      if (shopifyCountry) {
        return shopifyCountry;
      }
    } else if (window.nileDetectedCountry) {
      return window.nileDetectedCountry;
    }
    
    // Fallback to ipinfo.io
    try {
      const response = await fetch("https://ipinfo.io/json");
      const data = await response.json();
      return data.country;
    } catch (error) {
      console.error("Error fetching location:", error);
      return null;
    }
  }

  const userCountry = await getUserCountry();
  const currentEnv = getCurrentEnvironment();

  const chfStoreCountries = ["CH", "LI", "CA"];
  const gbpStoreCountries = ["GB"];
  const eurStoreCountries = [
    "AT",
    "BE",
    "BG",
    "HR",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "NO",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
  ];

  if (userCountry) {
    const isUserFromCHFCountry = chfStoreCountries.includes(userCountry);
    const isUserFromEURCountry = eurStoreCountries.includes(userCountry);
    const isUserFromGBPCountry = gbpStoreCountries.includes(userCountry);
    const isOnCHFStore = currentEnv.store === "chf";
    const isOnEURStore = currentEnv.store === "eur";
    const isOnGBPStore = currentEnv.store === "gbp";

    // User is on the CORRECT store - skip modal and set cookie silently
    if (isOnCHFStore && isUserFromCHFCountry) {
      const existingPrefs = window.NileCountryRedirect ? window.NileCountryRedirect.getNilePrefs() : null;
      
      // Only set cookie if no existing preferences (cookie-based check only)
      if (!existingPrefs && window.NileCountryRedirect) {
        const browserLanguage = getBrowserLanguage();
        const supportedLanguages = ["de", "en", "fr", "it"];
        const selectedLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : "de";
        
        window.NileCountryRedirect.setNilePrefs({
          country: userCountry,
          language: selectedLanguage,
          store: 'chf'
        });

        const currentPath = window.location.pathname;
        const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);

        if (selectedLanguage !== "de" && !hasLanguagePrefix) {
          const newPath = `/${selectedLanguage}${currentPath}`;
          const newUrl =
            window.location.origin +
            newPath +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
        }
      }
      return;
    }

    if (isOnEURStore && isUserFromEURCountry) {
      const existingPrefs = window.NileCountryRedirect ? window.NileCountryRedirect.getNilePrefs() : null;
      
      // Only set cookie if no existing preferences (cookie-based check only)
      if (!existingPrefs && window.NileCountryRedirect) {
        const browserLanguage = getBrowserLanguage();
        const supportedLanguages = ["de", "en", "fr", "it"];
        const selectedLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : "de";
        
        window.NileCountryRedirect.setNilePrefs({
          country: userCountry,
          language: selectedLanguage,
          store: 'eur'
        });

        const currentPath = window.location.pathname;
        const hasLanguagePrefix = LANG_PREFIX_RE.test(currentPath);

        if (selectedLanguage !== "de" && !hasLanguagePrefix) {
          const newPath = `/${selectedLanguage}${currentPath}`;
          const newUrl =
            window.location.origin +
            newPath +
            window.location.search +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
        }
      }
      return;
    }

    if (isOnGBPStore && isUserFromGBPCountry) {
      const existingPrefs = window.NileCountryRedirect ? window.NileCountryRedirect.getNilePrefs() : null;

      // GBP store is English-only; save preference silently if none exists.
      if (!existingPrefs && window.NileCountryRedirect) {
        window.NileCountryRedirect.setNilePrefs({
          country: userCountry,
          language: "en",
          store: 'gbp'
        });
      }
      return;
    }
  }

  // Check if user already has preferences set (cookie-based only)
  const existingCookiePrefs = window.NileCountryRedirect ? window.NileCountryRedirect.getNilePrefs() : null;

  // If cookie exists, don't show modal
  if (existingCookiePrefs) return;

  if (userCountry) {
    const isUserFromCHFCountry = chfStoreCountries.includes(userCountry);
    const isUserFromEURCountry = eurStoreCountries.includes(userCountry);
    const isUserFromGBPCountry = gbpStoreCountries.includes(userCountry);
    const isOnCHFStore = currentEnv.store === "chf";
    const isOnEURStore = currentEnv.store === "eur";
    const isOnGBPStore = currentEnv.store === "gbp";

    if (
      (isOnCHFStore && isUserFromCHFCountry) ||
      (isOnEURStore && isUserFromEURCountry) ||
      (isOnGBPStore && isUserFromGBPCountry)
    ) {
      return;
    }
  }

  function preselectUserCountry(countryCode) {
    if (!countryCode) return false;

    const countryForm = document.getElementById("LanguageModalCountryForm");
    if (!countryForm) return false;

    const countryInput = countryForm.querySelector(
      'input[name="country_code"]'
    );
    if (!countryInput) return false;

    let success = tryPreselectCountry(countryCode, countryInput);
    if (!success) {
      setTimeout(() => tryPreselectCountry(countryCode, countryInput), 200);
    }
    return success;
  }

  function tryPreselectCountry(countryCode, countryInput) {
    const availableCountries = document.querySelectorAll(
      "#ModalLanguageCountry-country-results .disclosure__link"
    );
    let countryFound = false;

    availableCountries.forEach((link) => {
      const linkCountryCode = link.getAttribute("data-value");
      if (linkCountryCode === countryCode) {
        countryFound = true;
        countryInput.value = countryCode;
        updateButtonTexts(
          countryCode,
          countryInput.form.querySelector('input[name="locale_code"]')?.value ||
            "de"
        );

        availableCountries.forEach((item) => {
          const checkmark = item.querySelector("span:first-child");
          if (checkmark) {
            if (item.getAttribute("data-value") === countryCode) {
              checkmark.classList.remove("visibility-hidden");
              item.setAttribute("aria-current", "true");
            } else {
              checkmark.classList.add("visibility-hidden");
              item.removeAttribute("aria-current");
            }
          }
        });
      }
    });

    return countryFound;
  }

  function preselectUserLanguage(languageCode) {
    if (!languageCode) return false;

    const languageForm = document.getElementById("LanguageModalLanguageForm");
    if (!languageForm) return false;

    const languageInput = languageForm.querySelector(
      'input[name="locale_code"]'
    );
    if (!languageInput) return false;

    let success = tryPreselectLanguage(languageCode, languageInput);
    if (!success) {
      setTimeout(() => tryPreselectLanguage(languageCode, languageInput), 200);
    }
    return success;
  }

  function tryPreselectLanguage(languageCode, languageInput) {
    const availableLanguages = document.querySelectorAll(
      "#ModalLanguageCountryList .disclosure__link"
    );
    let languageFound = false;

    availableLanguages.forEach((link) => {
      const linkLanguageCode = link.getAttribute("data-value");
      if (linkLanguageCode === languageCode) {
        languageFound = true;
        languageInput.value = languageCode;
        updateLanguageButtonOnly(languageCode);
        updateModalContent(languageCode);

        availableLanguages.forEach((item) => {
          const checkmark = item.querySelector("span:first-child");
          if (checkmark) {
            if (item.getAttribute("data-value") === languageCode) {
              checkmark.classList.remove("visibility-hidden");
              item.setAttribute("aria-current", "true");
            } else {
              checkmark.classList.add("visibility-hidden");
              item.removeAttribute("aria-current");
            }
          }
        });
      }
    });

    return languageFound;
  }

  function handleContinueButton() {
    try {
      const countryForm = document.getElementById("LanguageModalCountryForm");
      const languageForm = document.getElementById("LanguageModalLanguageForm");

      if (!countryForm) {
        console.error("Language Modal Error: Country form not found");
        return;
      }
      if (!languageForm) {
        console.error("Language Modal Error: Language form not found");
        return;
      }

      const countryInput = countryForm.querySelector(
        'input[name="country_code"]'
      );
      const languageInput = languageForm.querySelector(
        'input[name="locale_code"]'
      );

      if (!countryInput) {
        console.error("Language Modal Error: Country input not found");
        return;
      }
      if (!languageInput) {
        console.error("Language Modal Error: Language input not found");
        return;
      }

      const selectedCountry = countryInput.value;
      const selectedLanguage = languageInput.value;

      if (!selectedCountry) {
        console.error("Language Modal Error: No country selected");
        return;
      }
      if (!selectedLanguage) {
        console.error("Language Modal Error: No language selected");
        return;
      }

      // Get current language from URL
      const currentLanguage = window.NileCountryRedirect 
        ? window.NileCountryRedirect.getCurrentLanguageFromUrl() 
        : (window.location.pathname.match(LANG_CAPTURE_RE) || ['', 'de'])[1] || 'de';

      const currentPath = window.location.pathname;
      let pathWithoutLang = currentPath.replace(LANG_STRIP_OPT_RE, "");
      if (pathWithoutLang && !pathWithoutLang.startsWith("/"))
        pathWithoutLang = "/" + pathWithoutLang;
      if (pathWithoutLang === "/") pathWithoutLang = "";

      const currentEnv = getCurrentEnvironment();
      if (!currentEnv) {
        console.error(
          "Language Modal Error: Could not determine current environment"
        );
        return;
      }

      const chCountries = chfStoreCountries;
      const euCountries = eurStoreCountries;

      if (!chCountries || !Array.isArray(chCountries)) {
        console.error(
          "Language Modal Error: CHF countries array not available"
        );
        return;
      }
      if (!euCountries || !Array.isArray(euCountries)) {
        console.error(
          "Language Modal Error: EUR countries array not available"
        );
        return;
      }

      // Determine target store based on selected country
      let targetStore = 'unknown';
      if (chCountries.includes(selectedCountry)) {
        targetStore = 'chf';
      } else if (euCountries.includes(selectedCountry)) {
        targetStore = 'eur';
      } else if (selectedCountry === 'GB') {
        targetStore = 'gbp';
      }

      // Get current store
      const currentStore = window.NileCountryRedirect 
        ? window.NileCountryRedirect.getCurrentStore() 
        : currentEnv.store;

      // Check if we're staying on the same store
      const isSameStore = currentStore === targetStore || currentStore === 'unknown';
      const isSameLanguage = currentLanguage === selectedLanguage;

      // If same store AND same language - just save cookie and close modal, no reload
      if (isSameStore && isSameLanguage) {
        if (window.NileCountryRedirect) {
          window.NileCountryRedirect.setNilePrefs({
            country: selectedCountry,
            language: selectedLanguage,
            store: targetStore !== 'unknown' ? targetStore : currentStore
          });
        }
        
        // Close modal (same as closeModal function)
        const modal = document.querySelector(".language-modal-container");
        const backdrop = document.querySelector(".modal-backdrop");
        if (modal) modal.style.display = "none";
        if (backdrop) backdrop.style.display = "none";
        document.body.classList.remove("modal-open");
        
        // Clean up URL params without reload
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete("modal_country");
        newUrl.searchParams.delete("modal_language");
        newUrl.searchParams.delete("redirected");
        newUrl.searchParams.delete("country");
        if (newUrl.toString() !== window.location.href) {
          window.history.replaceState({}, "", newUrl);
        }
        
        console.log("Modal: Same store & language - saved cookie without reload");
        return;
      }

      let targetUrl;
      if (chCountries.includes(selectedCountry)) {
        if (currentEnv.isTest) {
          if (
            !window.themeSettings ||
            !window.themeSettings.test_ch_store_url
          ) {
            console.error(
              "Language Modal Error: Test CHF store URL not configured"
            );
            return;
          }
          targetUrl = window.themeSettings.test_ch_store_url;
        } else {
          if (!window.themeSettings || !window.themeSettings.ch_store_url) {
            console.error("Language Modal Error: CHF store URL not configured");
            return;
          }
          targetUrl = window.themeSettings.ch_store_url;
        }
      } else if (euCountries.includes(selectedCountry)) {
        if (currentEnv.isTest) {
          if (
            !window.themeSettings ||
            !window.themeSettings.test_eu_store_url
          ) {
            console.error(
              "Language Modal Error: Test EUR store URL not configured"
            );
            return;
          }
          targetUrl = window.themeSettings.test_eu_store_url;
        } else {
          if (!window.themeSettings || !window.themeSettings.eu_store_url) {
            console.error("Language Modal Error: EUR store URL not configured");
            return;
          }
          targetUrl = window.themeSettings.eu_store_url;
        }
      } else if (gbpStoreCountries.includes(selectedCountry)) {
        if (currentEnv.isTest) {
          if (
            !window.themeSettings ||
            !window.themeSettings.test_gbp_store_url
          ) {
            console.error(
              "Language Modal Error: Test GBP store URL not configured"
            );
            return;
          }
          targetUrl = window.themeSettings.test_gbp_store_url;
        } else {
          if (!window.themeSettings || !window.themeSettings.gbp_store_url) {
            console.error("Language Modal Error: GBP store URL not configured");
            return;
          }
          targetUrl = window.themeSettings.gbp_store_url;
        }
      } else {
        console.error(
          "Language Modal Error: Selected country not supported:",
          selectedCountry
        );
        return;
      }

      if (!targetUrl) {
        console.error("Language Modal Error: No target URL determined");
        return;
      }
      if (targetUrl.endsWith("/")) targetUrl = targetUrl.slice(0, -1);

      // Resolve the language for the TARGET store. Each store serves its default
      // locale at the root (no prefix); the GBP store is English-only.
      const storeDefaultLocale = { chf: "de", eur: "de", gbp: "en" };
      const targetDefaultLang = storeDefaultLocale[targetStore] || "de";
      // Carry the selected language; the target store applies it via
      // modal_language only if it publishes that language.
      let targetLang = selectedLanguage;

      // Preserve the current page (same-store language change or cross-store
      // switch). If the page is missing on the target store it 404s, and the 404
      // fallback in theme.liquid sends the visitor to the homepage.
      // Prefix a language only for stores known to publish de/en/fr/it (chf/eur).
      // gbp's locale set is not assumed: keep the bare path and let modal_language
      // upgrade it if published.
      let finalPath = pathWithoutLang === "/" ? "" : pathWithoutLang;
      if (targetStore !== "gbp" && targetLang !== targetDefaultLang) {
        finalPath = finalPath === "" ? `/${targetLang}` : `/${targetLang}${finalPath}`;
      }

      if (finalPath && !finalPath.startsWith("/")) finalPath = "/" + finalPath;

      let finalUrl;
      try {
        const baseUrl = finalPath === "" ? targetUrl : targetUrl + finalPath;
        const url = new URL(baseUrl);
        
        // Preserve existing query params (except our special ones)
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
          if (
            key !== "redirected" &&
            key !== "country" &&
            key !== "modal_language" &&
            key !== "modal_country"
          ) {
            url.searchParams.set(key, value);
          }
        });
        
        // Only add redirect params for cross-store redirects
        if (!isSameStore) {
          url.searchParams.set("redirected", "true");
          url.searchParams.set("country", selectedCountry);
          url.searchParams.set("modal_language", targetLang);
        }
        
        finalUrl = url.toString();
      } catch (urlError) {
        console.error(
          "Language Modal Error: Failed to construct final URL:",
          urlError
        );
        return;
      }

      if (finalUrl.endsWith("/")) finalUrl = finalUrl.slice(0, -1);

      // Set cookie via NileCountryRedirect (cookie only, no localStorage)
      try {
        if (window.NileCountryRedirect) {
          window.NileCountryRedirect.setNilePrefs({
            country: selectedCountry,
            language: targetLang,
            store: targetStore
          });
        } else {
          console.warn("NileCountryRedirect not available - preferences won't be saved");
        }
      } catch (storageError) {
        console.error(
          "Language Modal Error: Failed to save preferences:",
          storageError
        );
        // Don't return - still try to redirect
      }

      if (selectedLanguage) window.pendingLanguageUpdate = selectedLanguage;
      if (selectedCountry) window.pendingCountryUpdate = selectedCountry;

      if (isSameStore) {
        console.log("Modal: Same store, language change only - redirecting to:", finalUrl);
      } else {
        console.log("Modal: Cross-store redirect to:", finalUrl);
      }

      console.log("[NILE-DEBUG] modal continue -> window.location.href", {
        finalUrl, isSameStore, targetStore, selectedCountry, targetLang,
      });
      window.location.href = finalUrl;
    } catch (error) {
      console.error(
        "Language Modal Error: Unexpected error in handleContinueButton:",
        error
      );
    }
  }

  function showModal() {
    const modal = document.querySelector(".language-modal-container");
    const backdrop = document.querySelector(".modal-backdrop");
    const languageOptions = document.querySelector(".language-options");

    if (languageOptions) languageOptions.style.display = "flex";
    if (modal) modal.style.display = "flex";
    if (backdrop) backdrop.style.display = "block";
    document.body.classList.add("modal-open");

    const continueButton = document.querySelector(".language-modal-button");
    if (continueButton)
      continueButton.addEventListener("click", handleContinueButton);

    const browserLanguage = getBrowserLanguage();
    preselectUserCountry(userCountry);
    preselectUserLanguage(browserLanguage);
    updateModalContent(browserLanguage);
  }

  function closeModal() {
    const modal = document.querySelector(".language-modal-container");
    const backdrop = document.querySelector(".modal-backdrop");

    if (modal) modal.style.display = "none";
    if (backdrop) backdrop.style.display = "none";
    document.body.classList.remove("modal-open");
  }

  showModal();

  const languageLinks = document.querySelectorAll(
    "#ModalLanguageCountryList .disclosure__link"
  );
  languageLinks.forEach((link) => {
    link.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const languageCode = this.getAttribute("data-value");
      const languageInput = document.querySelector(
        "#LanguageModalLanguageForm input[name='locale_code']"
      );

      if (languageInput) {
        languageInput.value = languageCode;
        updateLanguageButtonOnly(languageCode);
        updateModalContent(languageCode);

        localStorage.setItem("selectedLanguage", languageCode);
        localStorage.setItem("userManuallySelected", languageCode);
      }
    });
  });

  const countryLinks = document.querySelectorAll(
    "#ModalLanguageCountry-country-results .disclosure__link"
  );
  countryLinks.forEach((link) => {
    link.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const countryCode = this.getAttribute("data-value");
      const countryInput = document.querySelector(
        "#LanguageModalCountryForm input[name='country_code']"
      );

      if (countryInput) {
        countryInput.value = countryCode;
        updateButtonTexts(
          countryCode,
          countryInput.form.querySelector('input[name="locale_code"]')?.value ||
            "de"
        );

        const allCountryLinks = document.querySelectorAll(
          "#ModalLanguageCountry-country-results .disclosure__link"
        );
        allCountryLinks.forEach((item) => {
          const checkmark = item.querySelector("span:first-child");
          if (checkmark) {
            if (item.getAttribute("data-value") === countryCode) {
              checkmark.classList.remove("visibility-hidden");
              item.setAttribute("aria-current", "true");
            } else {
              checkmark.classList.add("visibility-hidden");
              item.removeAttribute("aria-current");
            }
          }
        });

        if (countryCode && window.updateCountryOnly) {
          setTimeout(() => window.updateCountryOnly(countryCode), 100);
        }
      }
    });
  });
});
